/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IModuleStateProps, withModuleState, IModuleStateManager } from '@msdyn365-commerce-modules/checkout-utilities';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { computed, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ICheckoutInvoiceData } from './checkout-invoice.data';
import { ICheckoutInvoiceProps } from './checkout-invoice.props.autogenerated';

export interface ICheckoutInvoiceModuleProps extends ICheckoutInvoiceProps<ICheckoutInvoiceData>, IModuleStateProps {}

interface ICountriesOptions {
    key: string | undefined;
    value: string | undefined;
}
export interface ICheckoutInvoiceViewProps extends ICheckoutInvoiceModuleProps {
    CheckoutInvoice: IModuleProps;
    moduleProps: ICheckoutInvoiceModuleProps;
    countryRegions: ICountriesOptions[];
    moduleState: IModuleStateManager;
    onEdit?(): void;
    paymentVerificationRedirection: boolean;
}

/**
 *
 * CheckoutInvoice component
 * @extends {React.PureComponent<ICheckoutInvoiceProps<ICheckoutInvoiceData>>}
 */
// @ts-ignore
@withModuleState
@observer
class CheckoutInvoice extends React.Component<ICheckoutInvoiceModuleProps> {
    @computed get isDataReady(): boolean {
        return (this.props.data.checkout.result && this.props.data.checkout.status) === 'SUCCESS';
    }
    @computed get isPaymentVerificationRedirection(): boolean {
        const { requestFormData, query } = this.props.context.request;
        const redirection = query && query.pv === '1' && (requestFormData || query.redirectResult) ? true : false;
        return redirection;
    }

    public componentDidMount(): void {
        // @ts-ignore: Compiler not reconizing condition check for function params
        when(
            () => this.isDataReady,
            async () => {
                await this.init();
            }
        );
    }

    public render(): JSX.Element {
        const {
            config: { className },
            moduleState: { isReady }
        } = this.props;
        const countriesOptions: ICountriesOptions[] = [];
        this.props.data.countryRegions.result?.forEach((item, index: number) => {
            countriesOptions.push({
                key: item.CountryRegionId,
                value: item.ShortName
            });
        });

        const viewProps: ICheckoutInvoiceViewProps = {
            ...this.props,
            ...this.state,
            onEdit: this.onEdit,
            moduleProps: this.props,
            CheckoutInvoice: {
                moduleProps: this.props,
                className: classnames('ms-checkout-invoice', className, isReady ? 'show' : 'add')
            },
            countryRegions: countriesOptions,
            paymentVerificationRedirection: this.isPaymentVerificationRedirection
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private init = async (): Promise<void> => {
        this.props.moduleState.init({
            onEdit: this.onEdit
        });
        if (this.isPaymentVerificationRedirection) {
            this.props.moduleState.onReady();
        }
    };

    private onEdit = (): void => {
        this.props.moduleState.onUpdating();
    };
}

export default CheckoutInvoice;
